import { useEffect, useRef, useState } from "react";
import { fetchAllSurfaceTreatments, fetchMaterialProperties } from "../services/MaterialInfoService";
import { SurfaceTreatment, MaterialColor, MaterialDimension, MaterialProperties } from "../types/CommonTypes";
import QueryParams from "../utils/QueryParams";
import { useAssignment } from "./useAssignment";

const MOCK_STS: SurfaceTreatment[] = [
    { stId: '91',  stName: 'A603 Zn Class A',  density: '7.85', thickness: '20' },
    { stId: '25',  stName: 'A603 Zn Class B',  density: '7.70', thickness: '30' },
    { stId: '32',  stName: 'A603 Zn Class C',  density: '6.20', thickness: '50' },
    { stId: '412', stName: 'B200 Fe-Pb-Sn-12', density: '80.2', thickness: '12' },
    { stId: '413', stName: 'B200 Fe-Pb-Sn-25', density: '75.70', thickness: '25' },
    { stId: '414', stName: 'B200 Fe-Pb-Sn-50', density: '70.20', thickness: '50' },
];

export function useMaterialInfoService() {
    const { pendingMaterial, assignedMaterial, applyTMMaterial, assignedTreatment, hasTMSurface, applyInitialTreatment, alternateMaterials, selectedAlternateId, patchAlternateAbb } = useAssignment();
    const [allSTs, setAllSTs]             = useState<SurfaceTreatment[]>([]);
    const [validSTs, setValidSTs]         = useState<SurfaceTreatment[]>([]);
    const [colors, setColors]             = useState<MaterialColor[]>([]);
    const [dimensions, setDimensions]     = useState<MaterialDimension[]>([]);
    const [adb, setAdb]                   = useState<string | undefined>(undefined);
    const [version, setVersion]           = useState<string | undefined>(undefined);

    // Colors/dimensions for the committed primary material — kept separately so that
    // browsing an alternate in TM does not overwrite the primary display.
    const [primaryColors, setPrimaryColors]         = useState<MaterialColor[]>([]);
    const [primaryDimensions, setPrimaryDimensions] = useState<MaterialDimension[]>([]);

    // Holds the last successfully fetched properties along with the material id
    // they belong to. Kept in state so changes can be used as an effect dependency.
    const [fetchedProps, setFetchedProps] = useState<
        { forId: string; props: MaterialProperties } | undefined
    >(undefined);
    const requestGenerationRef = useRef(0);

    // Holds fetched properties for the currently selected alternate material.
    const [alternateFetchedProps, setAlternateFetchedProps] = useState<
        { forId: string; props: MaterialProperties } | undefined
    >(undefined);

    // True once the current pendingMaterial has been committed as an alternate in
    // this session.  Stays true until the user navigates to a different material in
    // TM (i.e. pendingMaterial.id changes).  This prevents the alternate material's
    // colors/dimensions from polluting the primary Colors/Dimensions tabs after the
    // alternate row has been deleted.
    const [pendingMaterialWasAlternate, setPendingMaterialWasAlternate] = useState(false);

    // Reset the flag whenever the user opens a different material in TM.
    useEffect(() => {
        setPendingMaterialWasAlternate(false);
    }, [pendingMaterial?.id]);

    // Set the flag while (or after) the current pendingMaterial is in alternates.
    useEffect(() => {
        if (alternateMaterials.some(a => a.materialId === pendingMaterial?.id)) {
            setPendingMaterialWasAlternate(true);
        }
    }, [alternateMaterials, pendingMaterial?.id]);

    const selectedAlternate = selectedAlternateId
        ? alternateMaterials.find(a => a.id === selectedAlternateId)
        : undefined;

    // True while the selected alternate's material properties are still being fetched.
    const isAlternateLoading = !!selectedAlternateId &&
        alternateFetchedProps?.forId !== selectedAlternate?.materialId;

    // Fetch the global ST list once on mount.
    useEffect(() => {
        if (QueryParams.mockST) {
            setAllSTs(MOCK_STS);
            return;
        }
        const subscription = fetchAllSurfaceTreatments().subscribe({
            next: setAllSTs,
            error: () => setAllSTs([]),
        });
        return () => subscription.unsubscribe();
    }, []);

    // Fetch material-specific info whenever pendingMaterial changes.
    // The cleanup function cancels any in-flight request for the previous material.
    useEffect(() => {
        if (QueryParams.mockST) {
            setFetchedProps({ forId: pendingMaterial?.id ?? '', props: { surfaceTreatments: MOCK_STS, colors: [], dimensions: [] } });
            return;
        }

        if (!pendingMaterial?.id) {
            setFetchedProps(undefined);
            return;
        }

        const id = pendingMaterial.id;
        const pendingName = pendingMaterial.name;
        const generation = ++requestGenerationRef.current;
        let cancelled = false;
        const subscription = fetchMaterialProperties(id).subscribe({
            next: (props) => {
                if (cancelled || generation !== requestGenerationRef.current) return;
                setFetchedProps({ forId: id, props });
                applyTMMaterial({
                    id,
                    name: props.name ?? pendingName,
                    density: props.density ?? '',
                    adb: props.adb,
                });
            },
            error: () => {
                if (cancelled || generation !== requestGenerationRef.current) return;
                setFetchedProps(undefined);
            },
        });

        return () => {
            cancelled = true;
            subscription.unsubscribe();
        };
    }, [pendingMaterial?.id]);

    // For any alternate rows that were loaded without abbId (e.g. saved before this
    // field existed), fetch their adb once and back-fill abbId.
    useEffect(() => {
        if (QueryParams.mockST) return;
        const missingIds = [...new Set(
            alternateMaterials
                .filter(a => a.abbId === undefined)
                .map(a => a.materialId)
        )];
        if (missingIds.length === 0) return;

        const subscriptions = missingIds.map(id =>
            fetchMaterialProperties(id).subscribe({
                next: (props) => patchAlternateAbb(id, props.adb),
                error: () => {},
            })
        );
        return () => subscriptions.forEach(s => s.unsubscribe());
    }, [alternateMaterials.map(a => `${a.materialId}:${a.abbId}`).join(',')]);

    // Fetch properties for the selected alternate's material so that Colors/Dimensions
    // tabs reflect that material when an alternate row is selected.
    useEffect(() => {
        if (QueryParams.mockST || !selectedAlternate?.materialId) {
            setAlternateFetchedProps(undefined);
            return;
        }
        const id = selectedAlternate.materialId;
        const subscription = fetchMaterialProperties(id).subscribe({
            next: (props) => {
                setAlternateFetchedProps({ forId: id, props });
                patchAlternateAbb(id, props.adb);
            },
            error: () => setAlternateFetchedProps(undefined),
        });
        return () => subscription.unsubscribe();
    }, [selectedAlternate?.materialId]);

    // Apply fetched properties to the display state only once the assigned material
    // matches the material the properties were fetched for.
    useEffect(() => {
        if (!assignedMaterial?.id || fetchedProps?.forId !== assignedMaterial.id) {
            setValidSTs([]);
            setColors([]);
            setDimensions([]);
            setAdb(undefined);
            setVersion(undefined);
            return;
        }
        const { props } = fetchedProps;
        setValidSTs(props.surfaceTreatments ?? []);
        setColors(props.colors ?? []);
        setDimensions(props.dimensions ?? []);
        setAdb(props.adb);
        setVersion(props.version);
        // Cache primary colors/dims when the fetched data belongs to the assigned material.
        if (fetchedProps.forId === assignedMaterial?.id) {
            setPrimaryColors(props.colors ?? []);
            setPrimaryDimensions(props.dimensions ?? []);
        }
    }, [assignedMaterial?.id, fetchedProps]);

    // Reconcile the surface treatment from the opening party against server data.
    // When parts arrive from Windchill/Creo they carry stId/stName/thickness/density
    // as previously stored — these may be stale or differently formatted compared to
    // what the server currently returns (e.g. stId prefix change).  Once the server
    // lists are available we look up the inbound stId and replace the assignment with
    // the authoritative entry.  The guard on hasTMSurface ensures we never override
    // a treatment the user has manually selected in this session.
    useEffect(() => {
        const stIdToFind = assignedTreatment?.stId;
        const thicknessToFind = assignedTreatment?.thickness;
        if (!stIdToFind || !thicknessToFind || hasTMSurface) return;

        const found =
            validSTs.find(st => st.stId === stIdToFind && st.thickness === thicknessToFind) ??
            allSTs.find(st => st.stId === stIdToFind && st.thickness === thicknessToFind) ??
            // Fallback: handle prefix mismatch (e.g. opening party stored "91",
            // server now returns prefixed "1SAA91").
            validSTs.find(st => st.stId.endsWith(stIdToFind) && st.stId.length > stIdToFind.length && st.thickness === thicknessToFind) ??
            allSTs.find(st => st.stId.endsWith(stIdToFind) && st.stId.length > stIdToFind.length && st.thickness === thicknessToFind);

        if (found) applyInitialTreatment(found);
    // applyInitialTreatment is a stable callback — omitting from deps is intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignedTreatment?.stId, assignedTreatment?.thickness, validSTs, allSTs, hasTMSurface]);

    // pendingMaterial may be an alternate material (user browsed it in TM to assign as
    // alternate). In that case don't let it pollute the primary Colors/Dimensions tabs.
    const pendingIsAlternate =
        pendingMaterialWasAlternate ||
        alternateMaterials.some(a => a.materialId === pendingMaterial?.id);

    // When an alternate row is selected, show its material's colors/dimensions;
    // when no alternate is selected but pending is an alternate, fall back to primary's;
    // otherwise show whatever TM currently has open (normal primary browse flow).
    const effectiveColors = selectedAlternateId
        ? (alternateFetchedProps?.props.colors ?? [])
        : pendingIsAlternate ? primaryColors : colors;
    const effectiveDimensions = selectedAlternateId
        ? (alternateFetchedProps?.props.dimensions ?? [])
        : pendingIsAlternate ? primaryDimensions : dimensions;

    return { allSTs, validSTs, colors: effectiveColors, dimensions: effectiveDimensions, adb, version, isAlternateLoading };
}